import api from '@/api'
import { getAttributionForPayload } from '@/utils/attribution'
import { trackCrmMetrikaGoal } from '@/utils/metrikaGoals'

const EXTERNAL_LEAD_ID_KEY = 'external_lead_id'
const SESSION_STARTED_AT_KEY = 'lead_session_started_at'

const ROLE_SCORES = {
  Собственник: 40,
  Руководитель: 35,
  'Тендерный специалист': 25,
  'Коммерческий директор': 30,
  Другое: 10,
}

const TENDERS_SCORES = {
  'До 5': 0,
  '5–20': 10,
  '20–50': 20,
  '50–200': 35,
  'Более 200': 50,
}

function safeStorage() {
  if (typeof window === 'undefined') return null
  return window.localStorage
}

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `lead_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

export function getOrCreateExternalLeadId() {
  const storage = safeStorage()
  if (!storage) return randomId()
  let id = storage.getItem(EXTERNAL_LEAD_ID_KEY)
  if (!id) {
    id = randomId()
    storage.setItem(EXTERNAL_LEAD_ID_KEY, id)
  }
  return id
}

export function hasExternalLeadId() {
  const storage = safeStorage()
  if (!storage) return false
  return Boolean(storage.getItem(EXTERNAL_LEAD_ID_KEY))
}

export function markLeadSessionStart() {
  const storage = safeStorage()
  if (!storage) return
  if (!storage.getItem(SESSION_STARTED_AT_KEY)) {
    storage.setItem(SESSION_STARTED_AT_KEY, String(Date.now()))
  }
}

export function getLeadSessionSeconds() {
  const storage = safeStorage()
  if (!storage) return 0
  const startedAt = Number(storage.getItem(SESSION_STARTED_AT_KEY) || 0)
  if (!startedAt) return 0
  return Math.max(0, Math.round((Date.now() - startedAt) / 1000))
}

export function buildLeadScore(payload = {}) {
  let score = 0
  score += ROLE_SCORES[payload.role] || 0
  score += TENDERS_SCORES[payload.tenders_per_month] || 0
  if (payload.repeated_visit) score += 15
  if (payload.registration_completed) score += 25
  if (payload.beta_activated) score += 40
  if (Number(payload.session_time || 0) >= 300) score += 10
  if (Number(payload.search_queries_count || 0) >= 2) score += 20
  const needs = String(payload.needs || '')
  if (needs.includes('Всё вместе')) score += 15
  return score
}

export function mapLeadCategory(score) {
  if (score >= 80) return 'A'
  if (score >= 40) return 'B'
  return 'C'
}

export async function sendLeadEvent(eventType, fields = {}) {
  const externalLeadId = getOrCreateExternalLeadId()
  const merged = {
    ...getAttributionForPayload(),
    ...fields,
    session_time: fields.session_time ?? getLeadSessionSeconds(),
  }
  const score = buildLeadScore(merged)
  const leadCategory = mapLeadCategory(score)
  const payload = {
    event_type: eventType,
    external_lead_id: externalLeadId,
    ...merged,
    lead_score: score,
    lead_category: leadCategory,
  }
  trackCrmMetrikaGoal(eventType)
  try {
    const { data } = await api.post('/crm/lead-events/', payload)
    const bitrixOk = data?.bitrix_sync_ok !== false
    return {
      ok: bitrixOk,
      externalLeadId,
      payload,
      data,
      error: bitrixOk ? null : new Error(data?.bitrix_error || 'bitrix_sync_failed'),
    }
  } catch (error) {
    const detail = error?.response?.data?.detail
    const message = detail
      ? (typeof detail === 'string' ? detail : JSON.stringify(detail))
      : error?.message
    return { ok: false, error, message, externalLeadId, payload }
  }
}
